import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { adminApi, ApiError, type AuditFilters } from "./api";
import { adminConfig } from "./config";
import {
  demoCustomers,
  demoIdentity,
  demoProductUsage,
  demoProducts,
  demoProductKeys,
  demoResults,
  demoSearch,
  demoTimelines,
} from "./demo-data";
import type { Activation, AuditResponse, ProductKeyMetadata } from "./types";
import type { LicenseViewFilters } from "./license-views";
import type {
  ListLicensesParams,
} from "./generated/model";

export const queryKeys = {
  identity: ["admin", "identity"] as const,
  products: ["admin", "products"] as const,
  productUsage: (segment: "paid" | "trial" | "combined", window: "24h" | "7d" | "30d") => ["admin", "product-usage", segment, window] as const,
  product: (productId: string) => ["admin", "product", productId] as const,
  customers: ["admin", "customers"] as const,
  search: (query: string) => ["admin", "search", query] as const,
  licenseIndex: (search: string, status: string, filters: LicenseViewFilters = {}) =>
    Object.keys(filters).length ? ["admin", "license-index", search, status, filters] as const : ["admin", "license-index", search, status] as const,
  activations: (licenseId: string) =>
    ["admin", "activations", licenseId] as const,
  productKeyActivations: (licenseId: string, productKeyId: string) =>
    ["admin", "activations", licenseId, "product-key", productKeyId] as const,
  productKeys: (licenseId: string) =>
    ["admin", "product-keys", licenseId] as const,
  timeline: (licenseId: string) => ["admin", "timeline", licenseId] as const,
  audit: (filters: AuditFilters) => ["admin", "audit", filters] as const,
  accounts: ["admin", "accounts"] as const,
  productAnalysis: (kind: string, productId: string, segment: string, window: string) => ["admin", "analysis", kind, productId, segment, window] as const,
};

export function useCurrentIdentityQuery() {
  return useQuery({
    queryKey: queryKeys.identity,
    queryFn: () =>
      adminConfig.demoMode
        ? Promise.resolve(demoIdentity)
        : adminApi.currentIdentity(),
    staleTime: 5 * 60_000,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 1,
  });
}
export function useAdminAccountsQuery(enabled = true) {
  return useQuery({ queryKey: queryKeys.accounts, queryFn: () => adminApi.adminAccounts(), enabled: enabled && !adminConfig.demoMode, staleTime: 10_000 });
}

function demoAnalysis(segment: "paid" | "trial" | "combined", window: "24h" | "7d" | "30d") {
  const generated = new Date("2026-08-12T08:00:00Z");
  const hours = window === "24h" ? 24 : window === "7d" ? 7 * 24 : 30 * 24;
  const started = new Date(generated.getTime() - hours * 60 * 60 * 1000).toISOString();
  const metadata = { generated_at: generated.toISOString(), window_started_at: started, window, segment };
  return {
    platforms: { ...metadata, platforms: [{ value: "windows", count: 2 }, { value: "darwin", count: 1 }] },
    versions: { ...metadata, versions: [{ value: "1.8.1", count: 2, latest_reported_at: "2026-08-12T07:14:00Z", evidence_state: "fresh" as const }, { value: "unknown", count: 1, latest_reported_at: null, evidence_state: "unknown" as const }] },
    trend: { ...metadata, buckets: [{ started_at: new Date(generated.getTime() - 60 * 60 * 1000).toISOString(), ended_at: generated.toISOString(), created: 1, deactivated: 0, revoked: 0 }] },
  };
}
export function useProductAnalysisQueries(productId: string, segment: "paid" | "trial" | "combined", window: "24h" | "7d" | "30d") {
  const params = { segment, window };
  const options = { enabled: Boolean(productId), staleTime: 15_000 };
  const platform = useQuery({ ...options, queryKey: queryKeys.productAnalysis("platform", productId, segment, window), queryFn: () => adminConfig.demoMode ? Promise.resolve(demoAnalysis(segment, window).platforms) : adminApi.productPlatforms(productId, params) });
  const version = useQuery({ ...options, queryKey: queryKeys.productAnalysis("version", productId, segment, window), queryFn: () => adminConfig.demoMode ? Promise.resolve(demoAnalysis(segment, window).versions) : adminApi.productVersions(productId, params) });
  const trend = useQuery({ ...options, queryKey: queryKeys.productAnalysis("trend", productId, segment, window), queryFn: () => adminConfig.demoMode ? Promise.resolve(demoAnalysis(segment, window).trend) : adminApi.productLifecycleTrend(productId, params) });
  return { platform, version, trend };
}

export function useProductUsageQuery(segment: "paid" | "trial" | "combined", window: "24h" | "7d" | "30d") {
  return useInfiniteQuery({
    queryKey: queryKeys.productUsage(segment, window),
    queryFn: ({ pageParam }) => adminConfig.demoMode
      ? Promise.resolve(segment === "combined"
        ? { ...demoProductUsage, window, segment, products: demoProductUsage.products.map((product) => ({ ...product, effective_active_licenses: 0, device_capacity: { available: false, current: "0", limit: "0", utilization_percent: null }, metric_availability: { effective_active_licenses: false, device_capacity: false } })) }
        : { ...demoProductUsage, window, segment })
      : adminApi.productUsage({ segment, window, limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    staleTime: 15_000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
}

export function useProductsQuery() {
  return useQuery({
    queryKey: queryKeys.products,
    queryFn: () =>
      adminConfig.demoMode
        ? Promise.resolve({ products: demoProducts })
        : adminApi.products(),
    staleTime: 15_000,
  });
}

export function useProductQuery(productId: string) {
  return useQuery({
    queryKey: queryKeys.product(productId),
    queryFn: async () => {
      if (!adminConfig.demoMode) return adminApi.product(productId);
      const product = demoProducts.find((item) => item.id === productId);
      if (!product) throw new Error("Product was not found in demo data.");
      return product;
    },
    enabled: Boolean(productId),
    staleTime: 15_000,
  });
}

export function useCustomersQuery() {
  return useQuery({
    queryKey: queryKeys.customers,
    queryFn: () =>
      adminConfig.demoMode
        ? Promise.resolve({ customers: demoCustomers })
        : adminApi.customers(),
    staleTime: 15_000,
  });
}

export function useCustomerQuery(customerId: string) {
  return useQuery({
    queryKey: ["admin", "customer", customerId] as const,
    queryFn: async () => {
      if (!adminConfig.demoMode) return adminApi.customer(customerId);
      const customer = demoCustomers.find((item) => item.id === customerId);
      if (!customer) throw new Error("Customer was not found in demo data.");
      return customer;
    },
    enabled: Boolean(customerId),
    staleTime: 15_000,
  });
}

export function useLicenseIndexQuery(
  search: string,
  status: string,
  enabled = true,
  filters: LicenseViewFilters = {},
) {
  const normalizedSearch = search.trim();
  const normalizedStatus = status === "all" ? "" : status;
  return useInfiniteQuery({
    queryKey: queryKeys.licenseIndex(normalizedSearch, normalizedStatus, filters),
    queryFn: ({ pageParam }) => {
      if (adminConfig.demoMode) {
        const results = demoSearch(normalizedSearch).results.filter(
          (result) =>
            (!normalizedStatus || result.license.status === normalizedStatus) &&
            (!filters.product_id || result.product.id === filters.product_id) &&
            (!filters.customer_id || result.customer.id === filters.customer_id) &&
            (!filters.at_capacity || result.active_activation_count >= result.license.policy.max_devices) &&
            (!filters.expires_from || (result.license.status === "active" && Date.parse(result.license.valid_from) <= Date.parse(filters.expires_from) && !!result.license.valid_until && Date.parse(result.license.valid_until) >= Date.parse(filters.expires_from) && Date.parse(result.license.valid_until) < Date.parse(filters.expires_before!))),
        );
        return Promise.resolve({
          licenses: results.map((result) => ({
            product: result.product,
            customer: result.customer,
            license: result.license,
            activation_count: result.activation_count,
            active_activation_count: result.active_activation_count,
            activations: result.activations,
            activations_truncated: result.activations_truncated,
            key_suffixes: result.key_suffixes,
            key_suffixes_truncated: result.key_suffixes_truncated,
          })),
          next_cursor: undefined,
        });
      }
      const params: ListLicensesParams = {
        limit: 50,
        ...filters,
        ...(normalizedSearch ? { q: normalizedSearch } : {}),
        ...(normalizedStatus
          ? {
              status:
                normalizedStatus as NonNullable<ListLicensesParams["status"]>,
            }
          : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      };
      return adminApi.licenses(params);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled,
    staleTime: 15_000,
  });
}

export function useSearchQuery(query: string) {
  const normalizedQuery = query.trim();
  return useQuery({
    queryKey: queryKeys.search(normalizedQuery),
    queryFn: () =>
      adminConfig.demoMode
        ? Promise.resolve(demoSearch(normalizedQuery))
        : adminApi.search(normalizedQuery),
    enabled: Boolean(normalizedQuery),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useActivationsQuery(
  licenseId: string,
  fallback: Activation[] = [],
) {
  return useInfiniteQuery({
    queryKey: queryKeys.activations(licenseId),
    queryFn: ({ pageParam }) =>
      adminConfig.demoMode
        ? Promise.resolve({ activations: fallback, next_cursor: undefined })
        : adminApi.activations(licenseId, {
            limit: 50,
            ...(pageParam ? { cursor: pageParam } : {}),
          }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: Boolean(licenseId),
    staleTime: 10_000,
  });
}

export function useProductKeyActivationsQuery(
  licenseId: string,
  productKeyId: string | null,
) {
  return useInfiniteQuery({
    queryKey: queryKeys.productKeyActivations(licenseId, productKeyId ?? ""),
    queryFn: ({ pageParam }) =>
      adminApi.activations(licenseId, {
        limit: 50,
        product_key_id: productKeyId ?? undefined,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: Boolean(licenseId && productKeyId) && !adminConfig.demoMode,
    staleTime: 10_000,
  });
}

export function useProductKeysQuery(licenseId: string, fallback: ProductKeyMetadata[] = []) {
  return useInfiniteQuery({
    queryKey: queryKeys.productKeys(licenseId),
    queryFn: ({ pageParam }) =>
      adminConfig.demoMode
        ? Promise.resolve({
            product_keys: fallback.length > 0
              ? fallback
              : demoProductKeys.filter((key) => key.license_id === licenseId),
            active_key_count: (fallback.length > 0 ? fallback : demoProductKeys.filter((key) => key.license_id === licenseId)).filter((key) => key.status === "active").length,
            next_cursor: undefined,
          })
        : adminApi.productKeys(licenseId, {
            limit: 100,
            ...(pageParam ? { cursor: pageParam } : {}),
          }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: Boolean(licenseId),
    staleTime: 10_000,
  });
}

export function useLicenseTimelineQuery(licenseId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.timeline(licenseId),
    queryFn: ({ pageParam }) =>
      adminConfig.demoMode
        ? Promise.resolve({
            events: demoTimelines[licenseId] ?? [],
            next_cursor: undefined,
          })
        : adminApi.licenseTimeline(licenseId, {
            limit: 50,
            ...(pageParam ? { cursor: pageParam } : {}),
          }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: Boolean(licenseId),
    staleTime: 10_000,
  });
}

// AUDIT_PAGE_SIZE is the server's maximum page; Load more follows next_cursor.
export const AUDIT_PAGE_SIZE = 100;

export function useAuditQuery(filters: AuditFilters = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.audit(filters),
    enabled,
    queryFn: ({ pageParam }) =>
      adminConfig.demoMode
        ? Promise.resolve({ events: [] } as AuditResponse)
        : adminApi.auditEvents(filters, { limit: AUDIT_PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    staleTime: 10_000,
  });
}
